<script>
import { mdiWrenchOutline } from '@mdi/js'
import { rmaHistoryByUserId, rmaDetailsByUid } from '@/api'
import RmaDetailsDialog from './rma_details_dialog.vue'
import DashboardSection from './dashboard_section.vue'

export default {
    name: 'RmasSection',
    components: { DashboardSection, RmaDetailsDialog },
    data() {
        return {
            rmas: [],
            rmaTotal: 0,
            rmasLoading: true,
            fetchingRmas: false,
            rmasError: '',
            rmaDetailsLoading: '',
            rmaDetailsError: '',
            singleRmaDetails: {}
        }
    },
    computed: {
        section() {
            return {
                id: 'rmas',
                title: 'RMAs',
                icon: mdiWrenchOutline,
                description: 'Follow your devices from arrival to repair and return.',
                link: '/your-account/rma-history',
                action: 'View RMAs',
                rows: this.rmas.map((rma) => ({
                    id: String(rma.uid),
                    title: `${rma.unit?.model || 'Getac device'} · RMA #${rma.uid}`,
                    detail: `Serial ${rma.unit?.serial || 'Unavailable'}`,
                    status: rma.customerStatus || 'Status unavailable',
                    tone: rma.closedAt && rma.status !== 'cancelled' ? 'green' : 'orange'
                })),
                footnote: this.rmasLoading
                    ? 'Loading your service requests…'
                    : this.rmasError
                    ? 'Service requests are temporarily unavailable'
                    : `Showing ${this.rmas.length} of ${this.rmaTotal} requests · Most recent first`
            }
        }
    },
    mounted() {
        this.fetchRmas()
    },
    methods: {
        emitSummary() {
            this.$emit('summary', {
                metric: this.rmasLoading ? '…' : this.rmasError ? '—' : String(this.rmaTotal).padStart(2, '0'),
                metricLabel: 'Total service requests'
            })
        },
        async fetchRmas() {
            if (this.fetchingRmas) return
            this.fetchingRmas = true
            this.rmasLoading = true
            this.rmasError = ''
            this.emitSummary()
            try {
                const { data } = await rmaHistoryByUserId({
                    sortBy: ['createdAt'],
                    sortDesc: [true],
                    page: 1,
                    itemsPerPage: 3,
                    filterText: '',
                    year: '',
                    month: '',
                    isDownload: false
                })
                this.rmas = Array.isArray(data?.rmas) ? data.rmas : []
                this.rmaTotal = Number(this.rmas[0]?.itemCount) || this.rmas.length
            } catch (error) {
                this.rmas = []
                this.rmaTotal = 0
                this.rmasError = 'Your RMAs could not be loaded. Please try again.'
            } finally {
                this.fetchingRmas = false
                this.rmasLoading = false
                this.emitSummary()
            }
        },
        async exposeRmaDetails(uid) {
            if (this.rmaDetailsLoading) return
            this.rmaDetailsLoading = uid
            this.rmaDetailsError = ''
            try {
                const { data } = await rmaDetailsByUid({ uid })
                if (!data?.uid) throw new Error('RMA details unavailable')
                this.singleRmaDetails = data
                this.$refs.rmaDetailsDialog.open()
            } catch (error) {
                this.rmaDetailsError = 'We could not open this RMA. Please try again or view your RMA history.'
            } finally {
                this.rmaDetailsLoading = ''
            }
        }
    }
}
</script>

<template>
    <dashboard-section
        :section="section"
        :loading="rmasLoading"
        :error="rmasError"
        :details-loading="rmaDetailsLoading"
        :details-error="rmaDetailsError"
        loading-text="Loading your RMAs…"
        empty-text="No service requests yet. Your RMAs will appear here after you submit a request."
        selectable
        @retry="fetchRmas"
        @select="exposeRmaDetails"
    >
        <template v-slot:details>
            <rma-details-dialog ref="rmaDetailsDialog" :rma-values="singleRmaDetails" @reloadHistory="fetchRmas" />
        </template>
    </dashboard-section>
</template>
